import React, { useMemo, useCallback, useEffect, useRef } from 'react';
import {
	Box,
	IconButton,
	Button,
	TextField,
	Select,
	MenuItem,
	FormControl,
	Grid,
	SelectChangeEvent,
	PopoverActions
} from '@mui/material';
import { Add as AddIcon, Close as CloseIcon } from '@mui/icons-material';
import { IEditorMessages } from '../../../../types';
import { MIN_EVENTS, MAX_EVENTS } from './constants';
import { EventRow } from '@opengov/stories-shared/types';
import { styles } from './ProgressTracker.styles';
import { useProgressTrackerSettingsContext } from '../../StoriesSettings/ProgressTrackerSettings/context/useProgressTrackerSettingsContext';
import { ProgressTrackerSetting } from '../../StoriesSettings/ProgressTrackerSettings/hooks/progressTracker/useFetchProgressTrackerSettings';

interface EventRowProps {
	event: EventRow;
	messages: IEditorMessages;
	onUpdateEvent: (id: string, field: keyof EventRow, value: string) => void;
	onRemoveEvent: (id: string) => void;
	canRemove?: boolean;
}

interface AddEventButtonProps {
	onAddEvent: () => void;
	messages: IEditorMessages;
}

export interface IUpdateEventPayload {
	id: string;
	field: keyof EventRow;
	value: string;
}

interface IEventsListProps {
	events: EventRow[];
	messages: IEditorMessages;
	onUpdateEvent: (payload: IUpdateEventPayload) => void;
	onRemoveEvent: (id: string) => void;
	onAddEvent: () => void;
}

const EventRowComponent = React.memo(({ event, messages, onUpdateEvent, onRemoveEvent, canRemove, }: EventRowProps) => {
	const { progressTrackerSettings: statusSettings } = useProgressTrackerSettingsContext();
	const selectRef = useRef<HTMLDivElement>(null);
	const statusMenuActionRef = useRef<PopoverActions>(null);

	useEffect(() => {
		const editorWindow = selectRef.current?.ownerDocument.defaultView;
		if (!editorWindow) {
			return undefined;
		}
		const reposition = () => statusMenuActionRef.current?.updatePosition();
		editorWindow.addEventListener('scroll', reposition, true);
		return () => editorWindow.removeEventListener('scroll', reposition, true);
	}, []);

	const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const newValue = e.target.value;
		onUpdateEvent(event.id, 'title', newValue);
	};

	const handleDescriptionChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const newValue = e.target.value;
		onUpdateEvent(event.id, 'description', newValue);
	};

	const handleStatusChange = (e: SelectChangeEvent<string>) => {
		const newValue = e.target.value;
		onUpdateEvent(event.id, 'status', newValue);
	};

	const handleRemove = () => {
		onRemoveEvent(event.id);
	};
	return (
		<Box key={event.id} sx={styles.gridContainer}>
			<Grid container spacing={1} sx={styles.eventRowContainer}>
				<Grid item xs={3}>
					<TextField
						fullWidth
						placeholder={messages.eventTitlePlaceholder}
						value={event.title}
						onChange={handleTitleChange}
						size="small"
						sx={styles.textField}
					/>
				</Grid>
				<Grid item xs={7}>
					<TextField
						fullWidth
						placeholder={messages.eventDescriptionPlaceholder}
						value={event.description}
						onChange={handleDescriptionChange}
						size="small"
						sx={styles.textField}
					/>
				</Grid>
				<Grid item xs={2}>
					<Box sx={styles.statusContainer}>
						<FormControl fullWidth size="small">
							<Select
								ref={selectRef}
								value={event.status.id}
								onChange={handleStatusChange}
								sx={styles.selectStatus}
								MenuProps={{
									disableScrollLock: true,
									action: statusMenuActionRef,
									marginThreshold: null
								}}
							>
								{statusSettings.map((status: ProgressTrackerSetting) => (
									<MenuItem key={status.id} value={status.id}>
										<Box sx={styles.statusContainer}>
											<Box
												sx={{
													...styles.statusColor,
													backgroundColor: status.colorCode,
												}}
											/>
											{status.status}
										</Box>
									</MenuItem>
								))}
							</Select>
						</FormControl>
						{canRemove && (
							<IconButton
								onClick={handleRemove}
								color="default"
								sx={styles.closeIcon}
							>
								<CloseIcon />
							</IconButton>
						)}
					</Box>
				</Grid>
			</Grid>
		</Box>
	);
});

EventRowComponent.displayName = 'EventRowComponent';

const AddEventButton = React.memo(({ onAddEvent, messages }: AddEventButtonProps) => {
	const handleAddEvent = useCallback(() => {
		onAddEvent();
	}, [onAddEvent]);

	return (
		<Box sx={styles.addEventButtonContainer}>
			<Button
				startIcon={<AddIcon />}
				onClick={handleAddEvent}
				sx={styles.addEventButton}
			>
				{messages.addEvent}
			</Button>
		</Box>
	);
});

AddEventButton.displayName = 'AddEventButton';

export const EventsList = React.memo(({ events, messages, onUpdateEvent, onRemoveEvent, onAddEvent }: IEventsListProps) => {
	const eventCount = events.length;
	const canRemoveEvents = eventCount > MIN_EVENTS;
	const showAddButton = eventCount < MAX_EVENTS;
	const eventRows = useMemo(() =>
		events.map((event) => (
			<EventRowComponent
				key={event.id}
				event={event}
				messages={messages}
				onUpdateEvent={(id, field, value) => onUpdateEvent({ id, field, value })}
				onRemoveEvent={onRemoveEvent}
				canRemove={canRemoveEvents}
			/>
		)), [events, messages, onUpdateEvent, onRemoveEvent, canRemoveEvents]
	);

	return (
		<>
			<Box>
				{eventRows}
			</Box>
			{showAddButton && <AddEventButton onAddEvent={onAddEvent} messages={messages} />}
		</>
	);
});

EventsList.displayName = 'EventsList'; 